import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

function todayBne(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Brisbane', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}
function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}
function mondayOf(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return addDays(ymd, dow === 0 ? -6 : 1 - dow);
}
function fmtRange(ws: string): string {
  const f = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  };
  return `${f(ws)} – ${f(addDays(ws, 6))}`;
}
const money = (n: number) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n || 0);

const btn: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)', background: 'var(--color-surface)', cursor: 'pointer', fontSize: 'var(--font-size-sm)',
};
const card: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', background: 'var(--color-surface)', padding: 'var(--spacing-4)',
};

function Row({ label, value, sign, strong, hint }: { label: string; value: number; sign?: '-' | '='; strong?: boolean; hint?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0', borderTop: strong ? '2px solid var(--color-border)' : undefined }}>
      <span style={{ fontWeight: strong ? 700 : 500 }}>
        {sign === '-' ? '− ' : sign === '=' ? '= ' : ''}{label}
        {hint && <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}> {hint}</span>}
      </span>
      <span style={{ fontWeight: strong ? 700 : 600, fontVariantNumeric: 'tabular-nums', color: strong && value < 0 ? 'var(--color-error)' : strong ? 'var(--color-success, #2e7d32)' : undefined }}>
        {sign === '-' ? `(${money(value)})` : money(value)}
      </span>
    </div>
  );
}

// ─── KPI dashboard (top of Reports) ─────────────────────────────────────────
function firstOfMonth(ymd: string): string { const [y, m] = ymd.split('-'); return `${y}-${m}-01`; }
function lastOfMonth(ymd: string): string { const [y, m] = ymd.split('-').map(Number); return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10); }
function addMonths(ymd: string, n: number): string { const [y, m] = ymd.split('-').map(Number); return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 10); }
function monthLabel(ymd: string): string { const [y, m, d] = ymd.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' }); }

function KpiTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ ...card, padding: 'var(--spacing-3)' }}>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
function KpiGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 'var(--spacing-3)' }}>
      <h3 style={{ margin: '0 0 var(--spacing-2)', fontSize: 'var(--font-size-md)' }}>{title}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--spacing-2)' }}>{children}</div>
    </div>
  );
}

function KpiDashboard() {
  const [mode, setMode] = useState<'week' | 'month' | 'custom'>('week');
  const [from, setFrom] = useState(() => mondayOf(todayBne()));
  const [to, setTo] = useState(() => addDays(mondayOf(todayBne()), 6));

  const pick = (m: 'week' | 'month' | 'custom') => {
    setMode(m);
    if (m === 'week') { const ws = mondayOf(todayBne()); setFrom(ws); setTo(addDays(ws, 6)); }
    else if (m === 'month') { const t = todayBne(); setFrom(firstOfMonth(t)); setTo(lastOfMonth(t)); }
  };
  const shift = (dir: -1 | 1) => {
    if (mode === 'week') { const ws = addDays(from, dir * 7); setFrom(ws); setTo(addDays(ws, 6)); }
    else if (mode === 'month') { const f = addMonths(from, dir); setFrom(firstOfMonth(f)); setTo(lastOfMonth(f)); }
  };

  const { data, isLoading, isError } = useQuery({ queryKey: ['dashboard', from, to], queryFn: () => api.reportsDashboard(from, to) });
  const pctStr = (v: number | null | undefined) => (v == null ? '—' : `${v}%`);
  const rangeLabel = mode === 'week' ? fmtRange(from) : mode === 'month' ? monthLabel(from) : `${from} → ${to}`;

  return (
    <div style={{ ...card, marginTop: 'var(--spacing-3)', borderColor: 'var(--color-accent)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
        <h2 style={{ margin: 0 }}>📊 Business dashboard</h2>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {(['week', 'month', 'custom'] as const).map((m) => (
            <button key={m} onClick={() => pick(m)}
              style={{ ...btn, background: mode === m ? 'var(--color-accent)' : 'var(--color-surface)', color: mode === m ? 'var(--color-text-inverse)' : 'var(--color-text)', textTransform: 'capitalize' }}>
              {m}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-2)', flexWrap: 'wrap' }}>
        {mode === 'custom' ? (
          <>
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={btn} />
            <span>→</span>
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={btn} />
          </>
        ) : (
          <>
            <button style={btn} onClick={() => shift(-1)}>‹ Prev</button>
            <strong style={{ minWidth: 160, textAlign: 'center' }}>{rangeLabel}</strong>
            <button style={btn} onClick={() => shift(1)}>Next ›</button>
          </>
        )}
      </div>

      {isLoading && <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-3)' }}>Loading…</p>}
      {isError && <p style={{ color: 'var(--color-error)', marginTop: 'var(--spacing-3)' }}>Could not load the dashboard.</p>}
      {data && (
        <>
          <KpiGroup title="Pipeline">
            <KpiTile label="New enquiries" value={String(data.pipeline.newEnquiries)} sub="new contacts added" />
            <KpiTile label="Quotes sent" value={String(data.pipeline.quotesSent)} />
            <KpiTile label="Quotes accepted" value={String(data.pipeline.quotesAccepted)} />
            <KpiTile label="Quote conversion" value={pctStr(data.pipeline.quoteConversionPct)} sub="accepted ÷ sent" />
            <KpiTile label="Average job value" value={money(data.pipeline.avgJobValue)} sub="accepted quotes" />
          </KpiGroup>
          <KpiGroup title="Distribution">
            <KpiTile label="New partners contacted" value={String(data.distribution.newPartnersContacted)} />
            <KpiTile label="Active referral partners" value={String(data.distribution.activeReferralPartners)} sub="current" />
          </KpiGroup>
          <KpiGroup title="Customers">
            <KpiTile label="Reactivation messages sent" value={String(data.customers.reactivationMessagesSent)} sub="campaign sends" />
            <KpiTile label="Repeat bookings" value={String(data.customers.repeatBookings)} sub="returning customers" />
          </KpiGroup>
          <KpiGroup title="Operations">
            <KpiTile label="Jobs completed" value={String(data.operations.jobsCompleted)} />
            <KpiTile label="Rework %" value={pctStr(data.operations.reworkPct)} sub="callbacks ÷ jobs" />
            <KpiTile label="Gross margin" value={pctStr(data.operations.grossMarginPct)} sub={`${money(data.operations.grossProfit)} on ${money(data.operations.revenue)}`} />
          </KpiGroup>
        </>
      )}
    </div>
  );
}

export function WeeklyReport() {
  const qc = useQueryClient();
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayBne()));
  const [perJob, setPerJob] = useState('');
  const [storage, setStorage] = useState('');
  const [upContractor, setUpContractor] = useState<number | ''>('');
  const [upAmount, setUpAmount] = useState('');
  const [upDesc, setUpDesc] = useState('');
  const [upFile, setUpFile] = useState<{ filename: string; mimeType: string; fileBase64: string } | null>(null);

  const { data: report, isLoading } = useQuery({ queryKey: ['report', weekStart], queryFn: () => api.reportsWeekly(weekStart) });
  const { data: invoices = [] } = useQuery({ queryKey: ['contractor-invoices', weekStart], queryFn: () => api.contractorInvoices(weekStart) });
  const { data: contractors = [] } = useQuery({ queryKey: ['availability', weekStart], queryFn: () => api.availabilityWeek(weekStart) });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['report'] });
    qc.invalidateQueries({ queryKey: ['contractor-invoices'] });
  };

  const settingsMut = useMutation({
    mutationFn: (d: { perJobCost?: number; monthlyStorage?: number }) => api.reportsSetSettings(d),
    onSuccess: () => { setPerJob(''); setStorage(''); refresh(); },
  });
  const createMut = useMutation({
    mutationFn: () => api.contractorInvoiceCreate({
      contractorId: upContractor === '' ? null : Number(upContractor),
      weekStart, amount: upAmount ? Number(upAmount) : null, description: upDesc || null,
      ...(upFile ?? {}),
    }),
    onSuccess: () => { setUpContractor(''); setUpAmount(''); setUpDesc(''); setUpFile(null); refresh(); },
  });
  const delMut = useMutation({ mutationFn: (id: number) => api.contractorInvoiceDelete(id), onSuccess: refresh });
  const reviewMut = useMutation({
    mutationFn: (v: { id: number; amount?: number; status?: string; contractorId?: number | null }) => api.contractorInvoiceUpdate(v.id, v),
    onSuccess: refresh,
  });
  const gmailMut = useMutation({ mutationFn: () => api.contractorInvoicesFetchGmail(weekStart), onSuccess: refresh });
  const [syncMsg, setSyncMsg] = useState('');
  const syncMut = useMutation({
    mutationFn: () => api.billingSync(),
    onSuccess: (r) => { setSyncMsg(`Synced ${r.invoices} invoices, ${r.quotes} quotes from Xero.`); refresh(); },
    onError: (e: any) => setSyncMsg(e?.message || 'Sync failed'),
  });

  const days = useMemo(() => fmtRange(weekStart), [weekStart]);

  const onPickFile = (f: File | undefined) => {
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setUpFile({ filename: f.name, mimeType: f.type || 'application/pdf', fileBase64: String(r.result).split(',')[1] ?? '' });
    r.readAsDataURL(f);
  };

  return (
    <div style={{ padding: 'var(--spacing-4)', maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
        <div>
          <h1 style={{ margin: 0 }}>Reports</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--color-text-muted)' }}>Weekly profit · {days}</p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          <button style={btn} onClick={() => setWeekStart(addDays(weekStart, -7))}>‹ Prev</button>
          <button style={btn} onClick={() => setWeekStart(mondayOf(todayBne()))}>This week</button>
          <button style={btn} onClick={() => setWeekStart(addDays(weekStart, 7))}>Next ›</button>
          <button
            style={btn}
            disabled={syncMut.isPending}
            title="Pull the latest invoices and paid-status from Xero now"
            onClick={() => { setSyncMsg(''); syncMut.mutate(); }}
          >
            {syncMut.isPending ? 'Syncing…' : '⟳ Sync Xero'}
          </button>
        </div>
      </div>
      {syncMsg && (
        <p style={{ margin: '8px 0 0', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>{syncMsg}</p>
      )}

      {/* KPI dashboard — week / month / custom range. */}
      <KpiDashboard />

      <h2 style={{ margin: 'var(--spacing-4) 0 0' }}>Weekly P&amp;L</h2>
      {isLoading && <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}

      {/* Forward-looking: value of this week's booked jobs + profit margin */}
      {report && (
        <div style={{ ...card, marginTop: 'var(--spacing-3)', borderColor: 'var(--color-accent)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h3 style={{ margin: 0 }}>This week's jobs · {report.jobsBreakdown.length}</h3>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>projected</span>
          </div>
          {report.jobsBreakdown.length > 0 && (
            <div style={{ margin: 'var(--spacing-2) 0', fontSize: 'var(--font-size-sm)' }}>
              {report.jobsBreakdown.map((j, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--color-text-muted)', padding: '2px 0' }}>
                  <span>{j.customer || 'Job'}{j.docNumber ? ` · ${j.docNumber}` : ''}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{money(j.value)}</span>
                </div>
              ))}
            </div>
          )}
          <Row label="Expected to collect" value={report.expectedRevenue} strong />
          <Row label="GST" value={report.expectedGst} sign="-" hint="(est. 1/11)" />
          <Row label="Contractor payments" value={report.contractorPay} sign="-" />
          <Row label={`Job costs (${report.jobs} × ${money(report.perJobCost)})`} value={report.jobCost} sign="-" />
          <Row label="Storage (weekly)" value={report.storageWeekly} sign="-" />
          <Row label="Projected profit" value={report.projectedProfit} sign="=" strong />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 'var(--spacing-2)', paddingTop: 'var(--spacing-2)', borderTop: '1px dashed var(--color-border)' }}>
            <span style={{ fontWeight: 700 }}>Profit margin</span>
            <span style={{ fontWeight: 800, fontSize: '1.4em', color: report.marginPct < 0 ? 'var(--color-error)' : 'var(--color-success, #2e7d32)' }}>
              {report.marginPct.toFixed(1)}%
            </span>
          </div>
        </div>
      )}

      {report && (
        <div style={{ ...card, marginTop: 'var(--spacing-3)' }}>
          <h3 style={{ marginTop: 0, marginBottom: 'var(--spacing-2)' }}>Collected (actual)</h3>
          <Row label="Collected (paid invoices)" value={report.revenue} hint={`· ${report.paidInvoices.length} invoice(s)`} />
          <Row label="GST" value={report.gst} sign="-" hint="(est. 1/11)" />
          <Row label="Contractor payments" value={report.contractorPay} sign="-" />
          <Row
            label="Expenses (from Xero)"
            value={report.actualExpenses}
            sign="-"
            hint={report.expensesBreakdown?.length ? '· categorised' : '· none categorised yet'}
          />
          {(report.expensesBreakdown ?? []).map((e) => (
            <Row key={e.category} label={`   ${e.category}`} value={e.total} sign="-" hint="" />
          ))}
          <Row label="Profit" value={report.profit} sign="=" strong />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 'var(--spacing-2)', paddingTop: 'var(--spacing-2)', borderTop: '1px dashed var(--color-border)' }}>
            <span style={{ fontWeight: 700 }}>Profit margin</span>
            <span style={{ fontWeight: 800, fontSize: '1.4em', color: report.profit < 0 ? 'var(--color-error)' : 'var(--color-success, #2e7d32)' }}>
              {(report.revenue > 0 ? (report.profit / report.revenue) * 100 : 0).toFixed(1)}%
            </span>
          </div>
        </div>
      )}

      {report && (
        <div style={{ ...card, marginTop: 'var(--spacing-3)' }}>
          <h3 style={{ marginTop: 0 }}>Cost settings</h3>
          <div style={{ display: 'flex', gap: 'var(--spacing-4)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ fontSize: 'var(--font-size-sm)' }}>
              Per-job cost (petrol, soap…)
              <div style={{ marginTop: 4 }}>
                <input type="number" step="0.01" placeholder={String(report.perJobCost)} value={perJob} onChange={(e) => setPerJob(e.target.value)}
                  style={{ width: 110, padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)' }} />
              </div>
            </label>
            <label style={{ fontSize: 'var(--font-size-sm)' }}>
              Storage / month
              <div style={{ marginTop: 4 }}>
                <input type="number" step="0.01" placeholder={String(report.monthlyStorage)} value={storage} onChange={(e) => setStorage(e.target.value)}
                  style={{ width: 110, padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)' }} />
              </div>
            </label>
            <button
              style={{ ...btn, background: 'var(--color-accent)', color: 'var(--color-text-inverse)', borderColor: 'var(--color-accent)' }}
              disabled={settingsMut.isPending || (!perJob && !storage)}
              onClick={() => settingsMut.mutate({ ...(perJob ? { perJobCost: Number(perJob) } : {}), ...(storage ? { monthlyStorage: Number(storage) } : {}) })}
            >
              {settingsMut.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      <div style={{ ...card, marginTop: 'var(--spacing-3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>Contractor invoices · this week</h3>
          <button style={btn} disabled={gmailMut.isPending} onClick={() => gmailMut.mutate()} title="Scan the Gmail inbox for invoice emails this week">
            {gmailMut.isPending ? 'Fetching…' : '📥 Fetch from Gmail'}
          </button>
        </div>
        {gmailMut.data && (
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
            Scanned {gmailMut.data.scanned}, added {gmailMut.data.created}{gmailMut.data.needsReview ? `, ${gmailMut.data.needsReview} need review` : ''}.
          </p>
        )}
        {gmailMut.isError && <p style={{ color: 'var(--color-error)' }}>{(gmailMut.error as Error).message}</p>}

        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 'var(--spacing-2)' }}>
          <thead>
            <tr style={{ textAlign: 'left', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              <th style={{ padding: 4 }}>Contractor</th><th style={{ padding: 4 }}>Amount</th><th style={{ padding: 4 }}>Source</th><th style={{ padding: 4 }}>Detail</th><th></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} style={{ borderTop: '1px solid var(--color-border)', background: inv.status === 'NEEDS_REVIEW' ? 'rgba(254,197,83,0.12)' : undefined }}>
                <td style={{ padding: 4 }}>{inv.name || '—'}</td>
                <td style={{ padding: 4 }}>
                  {inv.status === 'NEEDS_REVIEW' ? (
                    <input type="number" step="0.01" placeholder="$ ?" defaultValue={inv.amount ?? ''} style={{ width: 90 }}
                      onBlur={(e) => e.target.value && reviewMut.mutate({ id: inv.id, amount: Number(e.target.value), status: 'CONFIRMED' })} />
                  ) : money(inv.amount ?? 0)}
                </td>
                <td style={{ padding: 4 }}>
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 999, background: inv.source === 'gmail' ? 'var(--color-light-blue,#8DD2E5)' : 'var(--color-surface-elevated)' }}>
                    {inv.source}{inv.status === 'NEEDS_REVIEW' ? ' · review' : ''}
                  </span>
                </td>
                <td style={{ padding: 4, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  {inv.gmailSubject || inv.description || ''}
                  {inv.hasFile && (
                    <button
                      type="button"
                      title="Open invoice file"
                      onClick={async () => {
                        // Open the tab synchronously (popup-blocker safe), then
                        // point it at the auth-fetched blob URL once it loads.
                        const w = window.open('', '_blank', 'noopener');
                        try {
                          const url = await api.contractorInvoiceFileUrl(inv.id);
                          if (w) w.location.href = url;
                          else window.open(url, '_blank', 'noopener');
                        } catch {
                          if (w) w.close();
                          console.error('Could not open invoice file', inv.id);
                        }
                      }}
                      style={{ marginLeft: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-accent)', fontSize: 'inherit' }}
                    >
                      📎
                    </button>
                  )}
                </td>
                <td style={{ padding: 4, textAlign: 'right' }}>
                  <button onClick={() => delMut.mutate(inv.id)} style={{ background: 'none', border: 'none', color: 'var(--color-error)', cursor: 'pointer' }}>✕</button>
                </td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 8, color: 'var(--color-text-muted)' }}>No contractor invoices for this week yet.</td></tr>
            )}
          </tbody>
        </table>

        <div style={{ marginTop: 'var(--spacing-3)', paddingTop: 'var(--spacing-3)', borderTop: '1px solid var(--color-border)', display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={upContractor} onChange={(e) => setUpContractor(e.target.value === '' ? '' : Number(e.target.value))} style={{ padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)' }}>
            <option value="">Contractor…</option>
            {contractors.map((c) => <option key={c.contractorId} value={c.contractorId}>{c.name}</option>)}
          </select>
          <input type="number" step="0.01" placeholder="Amount $" value={upAmount} onChange={(e) => setUpAmount(e.target.value)} style={{ width: 110, padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)' }} />
          <input placeholder="Note (optional)" value={upDesc} onChange={(e) => setUpDesc(e.target.value)} style={{ flex: 1, minWidth: 140, padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)' }} />
          <input type="file" accept="application/pdf,image/*" onChange={(e) => onPickFile(e.target.files?.[0])} style={{ fontSize: 'var(--font-size-xs)' }} />
          <button style={{ ...btn, background: 'var(--color-accent)', color: 'var(--color-text-inverse)', borderColor: 'var(--color-accent)' }} disabled={createMut.isPending || !upAmount} onClick={() => createMut.mutate()}>
            {createMut.isPending ? 'Adding…' : 'Add invoice'}
          </button>
        </div>
      </div>

      <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        Revenue counts invoices marked PAID dated in the week. GST is an estimate (1/11 of the GST-inclusive total) — confirm the BAS figure in Xero.
      </p>
    </div>
  );
}
